import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Target, Heart, Lightbulb, Users } from 'lucide-react';

export default function AboutPage() {
  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously push boundaries to create cutting-edge networking solutions.',
    },
    {
      icon: Heart,
      title: 'Sustainability',
      description: 'Committed to reducing waste with eco-friendly digital alternatives.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Building connections between professionals, artists, and businesses worldwide.',
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'Delivering premium quality in every card and every interaction.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
            About Smart Connections
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Redefining professional networking for the digital age
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-extrabold text-navy mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Founded in the Cayman Islands, Smart Connections was born from a simple vision: 
                  to make professional networking seamless, sustainable, and smart.
                </p>
                <p>
                  We recognized that traditional business cards were outdated, wasteful, and limited. 
                  Our NFC-enabled digital cards bridge the gap between physical presence and digital 
                  connectivity, creating meaningful connections with just a tap.
                </p>
                <p>
                  Today, we're proud to serve thousands of professionals, businesses, and artists 
                  worldwide, helping them make lasting impressions while reducing environmental impact.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-purple-custom rounded-[2.5rem] p-12 min-h-[400px] flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-extrabold text-navy mb-12">
            Our Mission
          </h2>
          <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            To empower professionals and businesses with innovative NFC technology that makes 
            networking instant, sustainable, and memorable - while supporting artists and 
            reducing environmental impact.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-extrabold text-navy mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-navy rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-extrabold text-navy mb-2">10K+</div>
              <div className="text-gray-600">Happy Customers</div>
            </div>
            <div>
              <div className="text-5xl font-extrabold text-navy mb-2">50K+</div>
              <div className="text-gray-600">Cards Delivered</div>
            </div>
            <div>
              <div className="text-5xl font-extrabold text-navy mb-2">25+</div>
              <div className="text-gray-600">Countries Served</div>
            </div>
            <div>
              <div className="text-5xl font-extrabold text-navy mb-2">100%</div>
              <div className="text-gray-600">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
